import { useState, useMemo } from "react";
import { Modal } from "@/shared/components/Modal";
import { Button } from "@/shared/components/form/Button";
import { InputText } from "@/shared/components/form/InputText";
import { InputSelect } from "@/shared/components/form/InputSelect";
import type {
  CreateOrganizationFormData,
  CreateOrganizationRequest,
} from "../types/organization-form.types";
import {
  OrganizationStatus,
  Provider,
} from "../types/organizations-api.types";
import useTranslations from "@/shared/lang/useTranslations";

interface CreateOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateOrganizationRequest) => Promise<void>;
  isLoading?: boolean;
}

type SelectOption = {
  label: string;
  value: string;
};

const initialFormData: CreateOrganizationFormData = {
  name: "",
  slug: "",
  email: "",
  phone: "",
  card_provider: Provider.BLOQUE,
};

const generateSlug = (name: string): string => {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // Remover acentos
    .replace(/[^a-z0-9\s-]/g, "") // Remover caracteres especiales
    .trim()
    .replace(/\s+/g, "-") // Reemplazar espacios con guiones
    .replace(/-+/g, "-"); // Reemplazar múltiples guiones con uno solo
};

export const CreateOrganizationModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
}: CreateOrganizationModalProps) => {
  const { t } = useTranslations("cards");
  const [formData, setFormData] = useState<CreateOrganizationFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [slugAutoGenerated, setSlugAutoGenerated] = useState(false);

  const cardProviderOptions: SelectOption[] = useMemo(
    () => [
      { label: t("organizations_provider_bloque"), value: Provider.BLOQUE },
      { label: t("organizations_provider_crypto_mate"), value: Provider.CRYPTO_MATE },
    ],
    [t]
  );

  const handleFieldChange = (
    field: keyof CreateOrganizationFormData,
    value: string
  ) => {
    setFormData((prev) => {
      const newData = { ...prev, [field]: value };

      if (field === "name" && (slugAutoGenerated || !prev.slug)) {
        newData.slug = generateSlug(value);
        setSlugAutoGenerated(true);
      }

      return newData;
    });

    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFieldBlur = (field: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const normalizePhone = (phone: string): string => {
    if (!phone) return phone;
    return phone.replace(/[\s\-().+]/g, "");
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t("create_organization_validation_name_required");
    } else if (formData.name.trim().length < 2) {
      newErrors.name = t("create_organization_validation_name_min_length");
    } else if (formData.name.trim().length > 255) {
      newErrors.name = t("create_organization_validation_name_max_length");
    }

    if (formData.slug && formData.slug.length > 255) {
      newErrors.slug = t("create_organization_validation_slug_max_length");
    }

    if (!formData.email.trim()) {
      newErrors.email = t("create_organization_validation_email_required");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t("create_organization_validation_email_invalid");
    } else if (formData.email.length > 255) {
      newErrors.email = t("create_organization_validation_email_max_length");
    }

    if (formData.phone && formData.phone.length > 20) {
      newErrors.phone = t("create_organization_validation_phone_max_length");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const allFields = Object.keys(formData) as Array<keyof CreateOrganizationFormData>;
    const newTouched: Record<string, boolean> = {};
    allFields.forEach((field) => {
      newTouched[field] = true;
    });
    setTouched(newTouched);

    if (!validateForm()) {
      return;
    }

    const normalizedPhone = formData.phone ? normalizePhone(formData.phone) : null;

    const payload: CreateOrganizationRequest = {
      name: formData.name.trim(),
      slug: formData.slug.trim() || null,
      email: formData.email.trim(),
      phone: normalizedPhone || null,
      status: OrganizationStatus.ACTIVE,
      card_provider: formData.card_provider,
    };

    await onSubmit(payload);
    setFormData(initialFormData);
    setSlugAutoGenerated(false);
    setErrors({});
    setTouched({});
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setSlugAutoGenerated(false);
    setErrors({});
    setTouched({});
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t("create_organization_modal_title")}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {t("create_organization_modal_button_cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isLoading}
            disabled={isLoading}
          >
            {t("create_organization_modal_button_create")}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-6 max-h-[75vh] overflow-y-auto px-2 py-2">
        <div className="space-y-4">
          <div className="border-b border-gray-200 dark:border-zinc-700 pb-3">
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t("create_organization_modal_description")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <InputText
              label={t("update_organization_label_name")}
              name="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleFieldChange("name", e.target.value)}
              onBlur={() => handleFieldBlur("name")}
              errorMessage={touched.name && errors.name ? errors.name : false}
              required
              maxLength={255}
            />

            <InputText
              label={t("update_organization_label_email")}
              name="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleFieldChange("email", e.target.value)}
              onBlur={() => handleFieldBlur("email")}
              errorMessage={touched.email && errors.email ? errors.email : false}
              required
              maxLength={255}
            />

            <InputText
              label={t("update_organization_label_slug")}
              name="slug"
              type="text"
              value={formData.slug}
              onChange={(e) => {
                setSlugAutoGenerated(false);
                handleFieldChange("slug", e.target.value);
              }}
              onBlur={() => handleFieldBlur("slug")}
              errorMessage={touched.slug && errors.slug ? errors.slug : false}
              maxLength={255}
              infoMessage={t("create_organization_info_slug")}
            />

            <InputSelect<SelectOption>
              label={t("update_organization_label_card_provider")}
              name="card_provider"
              options={cardProviderOptions}
              value={
                cardProviderOptions.find((opt) => opt.value === formData.card_provider) ||
                null
              }
              onChange={(selected) => {
                const value = Array.isArray(selected)
                  ? selected[0]?.value
                  : selected?.value;
                handleFieldChange("card_provider", value || Provider.BLOQUE);
              }}
              errorMessage={
                touched.card_provider && errors.card_provider
                  ? errors.card_provider
                  : false
              }
            />

            <InputText
              label={t("update_organization_label_phone")}
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleFieldChange("phone", e.target.value)}
              onBlur={() => handleFieldBlur("phone")}
              errorMessage={touched.phone && errors.phone ? errors.phone : false}
              maxLength={20}
              infoMessage={t("update_organization_info_phone")}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};
